import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Asset, AssetList, Group, ShiftSlot, ShiftSlotList } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardFoot,
  CardHead,
  CardPad,
  Empty,
  Field,
  Grid,
  IconBtn,
  Input,
  Modal,
  Note,
  PageHead,
  Row,
  Select,
  Table,
  ActionsCell,
  useAdminToast,
} from '../admin-kit';
import { LoadError, Loading } from '../werkvloer';
import { useSession } from '../../shared/session';
import { HandoverExplainer } from './handover-explainer';
import './handover-explainer.css';

/**
 * Machines en ploegen per lijn, voor de ploegoverdracht (Stap 39, OVD-1; herontworpen in BHR-6,
 * frame W6).
 *
 * Twee kaarten per lijn: de **ploegen** (in de API "diensten": uren en de ploeg die dan werkt) en
 * de **machines** die in een overdracht een stand krijgen. Uit de ploegen volgt wie de overdracht
 * krijgt - vandaar dat een dienst zonder ploeg er uitdrukkelijk bij zegt dat dan de hele lijn ze
 * krijgt.
 *
 * Buiten gebruik stellen en niet verwijderen: een overdracht van vorig jaar noemt deze machine.
 */
export function HandoverAdminPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const adminToast = useAdminToast();
  const [lineId, setLineId] = useState('');

  const groups = useQuery({
    queryKey: ['groups', 'all'],
    queryFn: async (): Promise<Group[]> => {
      const { data, error } = await api.GET('/api/v1/groups', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Group[] }).items;
    },
  });

  const lines = (groups.data ?? []).filter((group) => group.kind === 'line');
  const crews = (groups.data ?? []).filter((group) => group.kind === 'shift');

  // De eerste lijn meteen kiezen: met één lijn is een keuzelijst een klik voor niets.
  useEffect(() => {
    if (!lineId && lines[0]) setLineId(lines[0].id);
  }, [lineId, lines]);

  return (
    <AdminPage>
      <PageHead
        title={t('handoverAdmin.title')}
        sub={t('beheer.werkvloer.handover.sub')}
        actions={
          lines.length > 0 ? (
            <div className="a-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <label className="a-label" htmlFor="handover-line">
                {t('handoverAdmin.line')}
              </label>
              <div style={{ width: 180 }}>
                <Select
                  id="handover-line"
                  value={lineId}
                  data-testid="handover-line"
                  onChange={(event) => setLineId(event.target.value)}
                >
                  {lines.map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ) : null
        }
      />

      <HandoverExplainer />

      {groups.isPending ? (
        <Card>
          <Loading />
        </Card>
      ) : groups.isError ? (
        <Card>
          <LoadError onRetry={() => void groups.refetch()} error={groups.error} />
        </Card>
      ) : lines.length === 0 ? (
        <Card>
          <Empty
            icon={<AdminIcon name="rotate" size={24} />}
            title={t('handoverAdmin.noLinesTitle')}
            text={t('handoverAdmin.noLinesHint')}
          />
        </Card>
      ) : lineId ? (
        <>
          <Grid variant="two">
            <SlotsCard lineId={lineId} crews={crews} onSaved={adminToast.show} />
            <AssetsCard lineId={lineId} />
          </Grid>
          <Card>
            <CardPad>
              <Note tone="info">{t('beheer.werkvloer.handover.assetsNote')}</Note>
            </CardPad>
          </Card>
        </>
      ) : null}
      {adminToast.element}
    </AdminPage>
  );
}

/** Een einde vóór het begin is geen fout maar een nachtploeg. */
function isNight(slot: { startsAt: string; endsAt: string }): boolean {
  return slot.endsAt <= slot.startsAt;
}

function SlotsCard({
  lineId,
  crews,
  onSaved,
}: {
  lineId: string;
  crews: Group[];
  onSaved: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [bewerk, setBewerk] = useState<ShiftSlot | 'nieuw' | null>(null);

  const slots = useQuery({
    queryKey: ['shift-slots', lineId],
    queryFn: async (): Promise<ShiftSlot[]> => {
      const { data, error } = await api.GET('/api/v1/shift-slots', {
        params: { query: { lineId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ShiftSlotList).items;
    },
  });

  return (
    <Card testId="handover-slots">
      <CardHead
        title={t('beheer.werkvloer.handover.slots')}
        count={slots.data?.length}
        actions={
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            onClick={() => setBewerk('nieuw')}
            testId="slot-new"
          >
            {t('beheer.werkvloer.handover.addSlot')}
          </Btn>
        }
      />

      {slots.isPending ? (
        <Loading />
      ) : slots.isError ? (
        <LoadError onRetry={() => void slots.refetch()} error={slots.error} />
      ) : (slots.data ?? []).length === 0 ? (
        <Empty
          icon={<AdminIcon name="clock" size={24} />}
          title={t('beheer.werkvloer.handover.noSlotsTitle')}
          text={t('handoverAdmin.noSlotsHint')}
        />
      ) : (
        <Table label={t('beheer.werkvloer.handover.slots')}>
          <thead>
            <tr>
              <th>{t('handoverAdmin.slotName')}</th>
              <th>{t('handoverAdmin.times')}</th>
              <th>{t('handoverAdmin.crew')}</th>
              <th>{t('handoverAdmin.status')}</th>
              <th className="act">
                <span className="a-sr">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(slots.data ?? []).map((slot) => (
              <tr key={slot.id} data-testid="slot-row">
                <td>
                  <span className="a-name">{slot.name}</span>
                  {/* Geen maantje meer (FBK-3): de uren zeggen het al, de regel onder de tabel ook. */}
                  {isNight(slot) ? (
                    <span className="a-sr"> ({t('beheer.werkvloer.handover.night')})</span>
                  ) : null}
                </td>
                <td>
                  {slot.startsAt}–{slot.endsAt}
                </td>
                <td>{slot.crewGroupName ?? t('handoverAdmin.wholeLine')}</td>
                <td>
                  <Badge tone={slot.active ? 'ok' : 'neutral'}>
                    {slot.active ? t('handoverAdmin.active') : t('handoverAdmin.inactive')}
                  </Badge>
                </td>
                <ActionsCell>
                  <IconBtn
                    label={t('common.edit')}
                    icon={<AdminIcon name="pencil" />}
                    onClick={() => setBewerk(slot)}
                    testId="slot-edit"
                  />
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <CardFoot>
        <span>{t('beheer.werkvloer.handover.nightFoot')}</span>
      </CardFoot>

      {bewerk ? (
        <SlotModal
          lineId={lineId}
          crews={crews}
          slot={bewerk === 'nieuw' ? undefined : bewerk}
          onClose={() => setBewerk(null)}
          onSaved={async () => {
            setBewerk(null);
            await queryClient.invalidateQueries({ queryKey: ['shift-slots', lineId] });
            onSaved(t('handoverAdmin.saved'));
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * Een ploeg aanmaken of wijzigen, in een venster (korte vorm).
 *
 * Een einde vóór het begin is geen fout maar een nachtploeg; dat zegt het venster erbij, want
 * "22:00–06:00" ziet er voor wie het intikt uit als een vergissing.
 */
function SlotModal({
  lineId,
  crews,
  slot,
  onClose,
  onSaved,
}: {
  lineId: string;
  crews: Group[];
  slot?: ShiftSlot;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();

  const [name, setName] = useState(slot?.name ?? '');
  const [startsAt, setStartsAt] = useState(slot?.startsAt ?? '06:00');
  const [endsAt, setEndsAt] = useState(slot?.endsAt ?? '14:00');
  const [crewGroupId, setCrewGroupId] = useState(slot?.crewGroupId ?? '');
  const [active, setActive] = useState(slot?.active ?? true);

  const geldig = /^([01]\d|2[0-3]):[0-5]\d$/;
  const klopt = name.trim().length > 0 && geldig.test(startsAt) && geldig.test(endsAt);
  const nacht = klopt && isNight({ startsAt, endsAt });

  const bewaar = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        startsAt,
        endsAt,
        crewGroupId: crewGroupId || null,
      };
      if (slot) {
        const { error } = await api.PATCH('/api/v1/shift-slots/{slotId}', {
          params: { path: { slotId: slot.id } },
          body: { ...body, active } as never,
        });
        if (error) throw error;
        return;
      }
      const { error } = await api.POST('/api/v1/shift-slots', {
        body: { ...body, lineId } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => void onSaved(),
    onError: (error: unknown) =>
      toast.show({
        tone: 'danger',
        title: t('handoverAdmin.failed'),
        description: problemDetail(error) ?? undefined,
      }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={
        slot ? t('beheer.werkvloer.handover.editSlot') : t('beheer.werkvloer.handover.addSlot')
      }
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={!klopt || startsAt === endsAt || bewaar.isPending}
            onClick={() => bewaar.mutate()}
            testId="slot-save"
          >
            {t('common.save')}
          </Btn>
        </>
      }
    >
      <Field label={t('handoverAdmin.slotName')}>
        {(props) => (
          <Input
            {...props}
            placeholder={t('handoverAdmin.slotNamePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="slot-name"
          />
        )}
      </Field>
      <Row>
        <Field label={t('handoverAdmin.startsAt')}>
          {(props) => (
            <Input
              {...props}
              type="time"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              data-testid="slot-starts"
            />
          )}
        </Field>
        <Field label={t('handoverAdmin.endsAt')}>
          {(props) => (
            <Input
              {...props}
              type="time"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
              data-testid="slot-ends"
            />
          )}
        </Field>
      </Row>
      {nacht ? (
        <Note tone="info" icon={<AdminIcon name="moon" size={18} />}>
          {t('handoverAdmin.overnight')}
        </Note>
      ) : null}
      <Field label={t('handoverAdmin.crew')} hint={t('handoverAdmin.crewHint')}>
        {(props) => (
          <Select
            {...props}
            value={crewGroupId}
            onChange={(event) => setCrewGroupId(event.target.value)}
            data-testid="slot-crew"
          >
            <option value="">{t('handoverAdmin.wholeLine')}</option>
            {crews.map((crew) => (
              <option key={crew.id} value={crew.id}>
                {crew.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {slot ? (
        <Field label={t('handoverAdmin.status')}>
          {(props) => (
            <Select
              {...props}
              value={active ? 'active' : 'inactive'}
              onChange={(event) => setActive(event.target.value === 'active')}
              data-testid="slot-status"
            >
              <option value="active">{t('handoverAdmin.active')}</option>
              <option value="inactive">{t('handoverAdmin.inactive')}</option>
            </Select>
          )}
        </Field>
      ) : null}
    </Modal>
  );
}

function AssetsCard({ lineId }: { lineId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [naam, setNaam] = useState('');

  const assets = useQuery({
    queryKey: ['assets', lineId],
    queryFn: async (): Promise<Asset[]> => {
      const { data, error } = await api.GET('/api/v1/assets', {
        params: { query: { lineId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as AssetList).items;
    },
  });

  const vernieuw = () => queryClient.invalidateQueries({ queryKey: ['assets', lineId] });
  const fout = (error: unknown) =>
    toast.show({
      tone: 'danger',
      title: t('handoverAdmin.failed'),
      description: problemDetail(error) ?? undefined,
    });

  const voegToe = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/assets', {
        body: { lineId, name: naam.trim() } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setNaam('');
      await vernieuw();
    },
    onError: fout,
  });

  const zetActief = useMutation({
    mutationFn: async (input: { assetId: string; active: boolean }) => {
      const { error } = await api.PATCH('/api/v1/assets/{assetId}', {
        params: { path: { assetId: input.assetId } },
        body: { active: input.active } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => void vernieuw(),
    onError: fout,
  });

  return (
    <Card testId="handover-assets">
      <CardHead title={t('handoverAdmin.assets')} count={assets.data?.length} />

      {assets.isPending ? (
        <Loading />
      ) : assets.isError ? (
        <LoadError onRetry={() => void assets.refetch()} error={assets.error} />
      ) : (assets.data ?? []).length === 0 ? (
        <Empty
          icon={<AdminIcon name="rotate" size={24} />}
          title={t('handoverAdmin.noAssetsTitle')}
          text={t('handoverAdmin.noAssetsHint')}
        />
      ) : (
        <Table label={t('handoverAdmin.assets')}>
          <thead>
            <tr>
              <th>{t('handoverAdmin.assetName')}</th>
              <th>{t('handoverAdmin.status')}</th>
              <th className="act">
                <span className="a-sr">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(assets.data ?? []).map((asset) => (
              <tr key={asset.id} data-testid="asset-row">
                <td>{asset.name}</td>
                <td>
                  <Badge tone={asset.active ? 'ok' : 'neutral'}>
                    {asset.active
                      ? t('beheer.werkvloer.handover.inUse')
                      : t('beheer.werkvloer.handover.outOfUse')}
                  </Badge>
                </td>
                <ActionsCell>
                  <Btn
                    variant="ghost"
                    size="sm"
                    disabled={zetActief.isPending}
                    onClick={() => zetActief.mutate({ assetId: asset.id, active: !asset.active })}
                  >
                    {asset.active
                      ? t('beheer.werkvloer.handover.deactivate')
                      : t('beheer.werkvloer.handover.activate')}
                  </Btn>
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <CardFoot>
        <form
          className="a-inline"
          style={{ width: '100%' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (naam.trim()) voegToe.mutate();
          }}
        >
          <Input
            aria-label={t('beheer.werkvloer.handover.assetNameLabel')}
            placeholder={t('beheer.werkvloer.handover.assetNameLabel')}
            value={naam}
            maxLength={80}
            onChange={(event) => setNaam(event.target.value)}
            data-testid="asset-name"
          />
          <Btn
            type="submit"
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            disabled={!naam.trim() || voegToe.isPending}
            testId="asset-add"
          >
            {t('handoverAdmin.addAsset')}
          </Btn>
        </form>
      </CardFoot>
    </Card>
  );
}
